import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function Journal() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/posts`)
      .then((r) => r.json())
      .then((d) => {
        setPosts(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <>
      <Navbar />
      <main data-testid="journal-page" className="mx-auto max-w-[1400px] px-6 pb-32 pt-32 md:px-12 md:pt-40">
        <Reveal>
          <p className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-plum">
            The Library Journal
          </p>
          <h1 className="max-w-3xl font-serif text-5xl leading-tight tracking-tight text-ink sm:text-6xl md:text-7xl">
            Reviews, field notes & <em className="text-plum">small convictions.</em>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate2 md:text-lg">
            What we're reading, what the children argued about, and what the
            studio tables taught us — written from inside the library.
          </p>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
          {loading && (
            <p data-testid="journal-loading" className="font-serif text-xl italic text-slate2">
              Opening the journal…
            </p>
          )}
          {!loading && posts.length === 0 && (
            <p data-testid="journal-empty" className="font-serif text-xl italic text-slate2">
              No articles yet — the first one is being written.
            </p>
          )}
          {posts.map((p, i) => (
            <Reveal key={p.id} delay={0.06 * i}>
              <Link to={`/journal/${p.slug}`} data-testid={`journal-card-${p.slug}`} className="group block">
                {p.cover_image && (
                  <div className="mb-6 overflow-hidden rounded-2xl">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="border-t-2 border-ink/15 pt-6 transition-colors duration-300 group-hover:border-plum">
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em]">
                    <span className="font-semibold text-terracotta">{p.tag}</span>
                    <span className="text-slate2">{fmtDate(p.published_at)}</span>
                  </div>
                  <h2 className="mt-4 font-serif text-2xl leading-snug text-ink transition-colors duration-300 group-hover:text-plum md:text-3xl">
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="mt-3 text-sm leading-relaxed text-slate2 md:text-base">{p.excerpt}</p>
                  )}
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-plum">
                    Read article
                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <Link
            to="/"
            data-testid="journal-back-home"
            className="mt-20 inline-flex items-center gap-2 text-sm font-semibold text-plum"
          >
            <ArrowLeft size={15} /> Back to the library
          </Link>
        </Reveal>
      </main>
      <Footer />
    </>
  );
}
